import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { VENDOR_PROFILE, MY_SHOPS } from 'api/graphql/user';
import { storage } from 'lib/storage';
import { STORAGE_KEYS } from 'lib/constants';

const VendorContext = createContext(null);

/**
 * Single source of truth for the ACTIVE vendor/shop.
 *
 * Each shop is its own "vendor" with its own id (`profile.id`). The currently
 * selected shop's id is the `vendorId` used by every catalog / orders /
 * earnings query. Switching shops just changes `activeVendorId` (persisted), so
 * all per-shop queries re-run for the new shop.
 *
 * - VENDOR_PROFILE → the default shop + productCount.
 * - MY_SHOPS(activeVendorId) → all sibling shops owned by the same owner.
 * Mounted inside the authenticated shell, so the queries only run once signed in.
 */
export function VendorProvider({ children }) {
  const profileQuery = useQuery(VENDOR_PROFILE, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const defaultProfile = profileQuery.data?.vendorProfile?.profile ?? null;
  const productCount = profileQuery.data?.vendorProfile?.productCount ?? null;

  const [activeVendorId, setActiveVendorIdState] = useState(() =>
    storage.get(STORAGE_KEYS.ACTIVE_SHOP),
  );

  // Default the active shop to the current vendor's shop once it loads.
  useEffect(() => {
    if (!activeVendorId && defaultProfile?.id) {
      setActiveVendorIdState(defaultProfile.id);
    }
  }, [activeVendorId, defaultProfile]);

  const setActiveVendorId = useCallback((id) => {
    if (!id) return;
    storage.set(STORAGE_KEYS.ACTIVE_SHOP, id);
    setActiveVendorIdState(id);
  }, []);

  const shopsQuery = useQuery(MY_SHOPS, {
    variables: { vendorId: activeVendorId },
    skip: !activeVendorId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
  const shops = useMemo(() => shopsQuery.data?.myShops ?? [], [shopsQuery.data]);

  // The active shop's full profile (from MY_SHOPS), falling back to the default.
  const activeProfile = useMemo(() => {
    const match = shops.find((shop) => shop.profile?.id === activeVendorId)?.profile;
    return match || defaultProfile;
  }, [shops, activeVendorId, defaultProfile]);

  const vendorId = activeVendorId ?? defaultProfile?.id ?? null;

  const refetch = useCallback(() => {
    profileQuery.refetch?.();
    shopsQuery.refetch?.();
  }, [profileQuery, shopsQuery]);

  const value = useMemo(
    () => ({
      vendorId,
      profile: activeProfile,
      shops,
      productCount,
      setActiveVendorId,
      // "loading" means the vendorId itself isn't resolved yet — consumers fold
      // this into their own loading so they don't run with a null id.
      loading: !vendorId && profileQuery.loading,
      shopsLoading: shopsQuery.loading,
      error: profileQuery.error,
      refetch,
    }),
    [
      vendorId,
      activeProfile,
      shops,
      productCount,
      setActiveVendorId,
      profileQuery.loading,
      profileQuery.error,
      shopsQuery.loading,
      refetch,
    ],
  );

  return <VendorContext.Provider value={value}>{children}</VendorContext.Provider>;
}

export function useVendor() {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendor must be used within a <VendorProvider>.');
  }
  return context;
}
